// ============================================================================
// estoqueStorage — CRUD do módulo Estoque (Supabase REST)
// ============================================================================
// 5 modelos: products, suppliers, stockMovements, purchaseOrders, inventoryCounts
// ============================================================================

import { SUPABASE_URL, restHeaders } from './secureRest.js';

const num = (v) => Number(v) || 0;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUUID = (id) => typeof id === 'string' && UUID_RE.test(id.trim());
const asUuidOrNull = (v) => (isUUID(v) ? v.trim() : null);

async function patchOrPost(table, id, row) {
  if (isUUID(id)) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(await r.text());
    return (await r.json())[0];
  }
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(row),
  });
  if (!r.ok) throw new Error(await r.text());
  return (await r.json())[0];
}

// ═══════════════════════════════════════════════════════════════════════════
// SUPPLIERS
// ═══════════════════════════════════════════════════════════════════════════

const supToApp = (r) => !r ? null : ({
  id: r.id, name: r.name || '', contact: r.contact || '', phone: r.phone || '',
  email: r.email || '', cnpj: r.cnpj || '', address: r.address || '', notes: r.notes || '',
  active: r.active !== false, createdAt: r.created_at, updatedAt: r.updated_at,
});

const supToDb = (s) => {
  const r = {};
  if (s.name !== undefined) r.name = s.name;
  if (s.contact !== undefined) r.contact = s.contact || null;
  if (s.phone !== undefined) r.phone = s.phone || null;
  if (s.email !== undefined) r.email = s.email || null;
  if (s.cnpj !== undefined) r.cnpj = s.cnpj || null;
  if (s.address !== undefined) r.address = s.address || null;
  if (s.notes !== undefined) r.notes = s.notes || null;
  if (s.active !== undefined) r.active = !!s.active;
  return r;
};

export async function listSuppliers() {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/suppliers?select=*&order=name.asc&limit=2000`, { headers: restHeaders() });
  return r.ok ? (await r.json()).map(supToApp) : [];
}

export async function upsertSupplier(s) {
  const row = { ...supToDb(s), updated_at: new Date().toISOString() };
  return supToApp(await patchOrPost('suppliers', s.id, row));
}

// ═══════════════════════════════════════════════════════════════════════════
// PRODUCTS
// ═══════════════════════════════════════════════════════════════════════════

const prodToApp = (r) => !r ? null : ({
  id: r.id, name: r.name || '', brand: r.brand || '', category: r.category || '',
  sku: r.sku || '', barcode: r.barcode || '', internalCode: r.internal_code || '',
  description: r.description || '',
  costPrice: num(r.cost_price), salePrice: num(r.sale_price),
  currentStock: num(r.current_stock), minStock: num(r.min_stock),
  unitId: r.unit_id || '', supplierId: r.supplier_id || '',
  // Comissão de venda do produto (vai pra quem lançou na comanda)
  commissionPct: num(r.commission_pct),
  active: r.active !== false, createdAt: r.created_at, updatedAt: r.updated_at,
});

const prodToDb = (p) => {
  const r = {};
  if (p.name !== undefined) r.name = p.name;
  if (p.brand !== undefined) r.brand = p.brand || null;
  if (p.category !== undefined) r.category = p.category || null;
  if (p.sku !== undefined) r.sku = p.sku || null;
  if (p.barcode !== undefined) r.barcode = p.barcode || null;
  if (p.internalCode !== undefined) r.internal_code = p.internalCode || null;
  if (p.description !== undefined) r.description = p.description || null;
  if (p.costPrice !== undefined) r.cost_price = num(p.costPrice);
  if (p.salePrice !== undefined) r.sale_price = num(p.salePrice);
  if (p.currentStock !== undefined) r.current_stock = num(p.currentStock);
  if (p.minStock !== undefined) r.min_stock = num(p.minStock);
  if (p.unitId !== undefined) r.unit_id = p.unitId || null;
  if (p.supplierId !== undefined) r.supplier_id = asUuidOrNull(p.supplierId);
  if (p.commissionPct !== undefined) r.commission_pct = num(p.commissionPct);
  if (p.active !== undefined) r.active = !!p.active;
  return r;
};

export async function listProducts() {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/products?select=*&order=name.asc&limit=5000`, { headers: restHeaders() });
  return r.ok ? (await r.json()).map(prodToApp) : [];
}

export async function upsertProduct(p) {
  const row = { ...prodToDb(p), updated_at: new Date().toISOString() };
  return prodToApp(await patchOrPost('products', p.id, row));
}

export async function deleteProduct(id) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/products?id=eq.${id}`, { method: 'DELETE', headers: restHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return true;
}

// ═══════════════════════════════════════════════════════════════════════════
// STOCK MOVEMENTS
// ═══════════════════════════════════════════════════════════════════════════

const smToApp = (r) => !r ? null : ({
  id: r.id, productId: r.product_id || '', productName: r.product_name || '',
  unitId: r.unit_id || '', type: r.type || 'entrada', reason: r.reason || '',
  quantity: num(r.quantity), supplierId: r.supplier_id || '',
  notes: r.notes || '', date: r.date || '', createdBy: r.created_by || '', createdAt: r.created_at,
});

const smToDb = (m) => {
  const r = {};
  if (m.productId !== undefined) r.product_id = asUuidOrNull(m.productId);
  if (m.productName !== undefined) r.product_name = m.productName || null;
  if (m.unitId !== undefined) r.unit_id = m.unitId || null;
  if (m.type !== undefined) r.type = m.type;
  if (m.reason !== undefined) r.reason = m.reason || null;
  if (m.quantity !== undefined) r.quantity = num(m.quantity);
  if (m.supplierId !== undefined) r.supplier_id = asUuidOrNull(m.supplierId);
  if (m.notes !== undefined) r.notes = m.notes || null;
  if (m.date !== undefined) r.date = m.date;
  if (m.createdBy !== undefined) r.created_by = m.createdBy || null;
  return r;
};

export async function listStockMovements({ limit = 5000 } = {}) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/stock_movements?select=*&order=date.desc,created_at.desc&limit=${limit}`, { headers: restHeaders() });
  return r.ok ? (await r.json()).map(smToApp) : [];
}

export async function upsertStockMovement(m) {
  const row = smToDb(m);
  return smToApp(await patchOrPost('stock_movements', m.id, row));
}

// ═══════════════════════════════════════════════════════════════════════════
// PURCHASE ORDERS
// ═══════════════════════════════════════════════════════════════════════════

const poToApp = (r) => !r ? null : ({
  id: r.id, number: r.number || '', supplierId: r.supplier_id || '',
  supplierName: r.supplier_name || '', unitId: r.unit_id || '',
  status: r.status || 'rascunho', items: r.items || [], total: num(r.total),
  expectedDate: r.expected_date || '', notes: r.notes || '',
  createdAt: r.created_at, updatedAt: r.updated_at,
});

const poToDb = (p) => {
  const r = {};
  if (p.number !== undefined) r.number = p.number || null;
  if (p.supplierId !== undefined) r.supplier_id = asUuidOrNull(p.supplierId);
  if (p.supplierName !== undefined) r.supplier_name = p.supplierName || null;
  if (p.unitId !== undefined) r.unit_id = p.unitId || null;
  if (p.status !== undefined) r.status = p.status;
  if (p.items !== undefined) r.items = p.items || [];
  if (p.total !== undefined) r.total = num(p.total);
  if (p.expectedDate !== undefined) r.expected_date = p.expectedDate || null;
  if (p.notes !== undefined) r.notes = p.notes || null;
  return r;
};

export async function listPurchaseOrders() {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/purchase_orders?select=*&order=created_at.desc&limit=2000`, { headers: restHeaders() });
  return r.ok ? (await r.json()).map(poToApp) : [];
}

export async function upsertPurchaseOrder(p) {
  const row = { ...poToDb(p), updated_at: new Date().toISOString() };
  return poToApp(await patchOrPost('purchase_orders', p.id, row));
}

// ═══════════════════════════════════════════════════════════════════════════
// INVENTORY COUNTS
// ═══════════════════════════════════════════════════════════════════════════

const icToApp = (r) => !r ? null : ({
  id: r.id, date: r.date || '', unitId: r.unit_id || '',
  status: r.status || 'em_andamento', items: r.items || [],
  totalDiff: num(r.total_diff), notes: r.notes || '',
  createdBy: r.created_by || '',
  finalizedAt: r.finalized_at || null, finalizedBy: r.finalized_by || '',
  createdAt: r.created_at, updatedAt: r.updated_at,
});

const icToDb = (c) => {
  const r = {};
  if (c.date !== undefined) r.date = c.date;
  if (c.unitId !== undefined) r.unit_id = c.unitId || null;
  if (c.status !== undefined) r.status = c.status;
  if (c.items !== undefined) r.items = c.items || [];
  if (c.totalDiff !== undefined) r.total_diff = num(c.totalDiff);
  if (c.notes !== undefined) r.notes = c.notes || null;
  if (c.createdBy !== undefined) r.created_by = c.createdBy || null;
  if (c.finalizedAt !== undefined) r.finalized_at = c.finalizedAt || null;
  if (c.finalizedBy !== undefined) r.finalized_by = c.finalizedBy || null;
  return r;
};

export async function listInventoryCounts() {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/inventory_counts?select=*&order=date.desc&limit=1000`, { headers: restHeaders() });
  return r.ok ? (await r.json()).map(icToApp) : [];
}

export async function upsertInventoryCount(c) {
  const row = { ...icToDb(c), updated_at: new Date().toISOString() };
  return icToApp(await patchOrPost('inventory_counts', c.id, row));
}
